import { useEffect, useMemo, useState, useTransition } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { resolveCreateDemandId } from '../lib/demandId.js';
import { demandChoiceFields, getLocalSettings } from '../lib/settingsStore.js';
import { useSaveToast } from './SaveToast.jsx';
import { formatMatterMoney } from '../lib/matterCurrency.js';

function formatCurrency(value) {
  return formatMatterMoney(value, 'USD');
}

function demoInvoices() {
  return [
    {
      id: '9001',
      docNumber: '5542',
      externalInvoiceId: 'demo-realm:9001',
      realmId: 'demo-realm',
      debtorName: 'Northwind Logistics LLC',
      debtorState: 'CA',
      amountOwed: 18450,
      dueDate: '2026-07-01',
      balance: 18450,
      imported: true,
      demandSent: false,
      matterId: 'm_1001',
      matterStatus: 'ATTORNEY_ASSIGNED',
      evidenceVaultUrl: 'quickbooks://invoice/demo-realm/9001',
    },
    {
      id: '9002',
      docNumber: '5610',
      externalInvoiceId: 'demo-realm:9002',
      realmId: 'demo-realm',
      debtorName: 'Summit Freight Co.',
      debtorState: 'TX',
      amountOwed: 9750,
      dueDate: '2026-07-12',
      balance: 9750,
      imported: false,
      demandSent: false,
      matterId: null,
      matterStatus: null,
      evidenceVaultUrl: 'quickbooks://invoice/demo-realm/9002',
    },
    {
      id: '9003',
      docNumber: '5688',
      externalInvoiceId: 'demo-realm:9003',
      realmId: 'demo-realm',
      debtorName: 'Lakeside Outfitters',
      debtorState: 'NY',
      amountOwed: 4200,
      dueDate: '2026-07-18',
      balance: 4200,
      imported: false,
      demandSent: false,
      matterId: null,
      matterStatus: null,
      evidenceVaultUrl: 'quickbooks://invoice/demo-realm/9003',
    },
  ];
}

export default function QuickBooksSendDrawer({ open, email, onClose, onDemandsSent }) {
  const { flashSave, clearSaveToast } = useSaveToast();
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [connected, setConnected] = useState(false);
  const [mock, setMock] = useState(false);
  const [error, setError] = useState('');
  const [pending, startTransition] = useTransition();

  const settings = getLocalSettings(email || '');

  useEffect(() => {
    if (!open || !email) return;
    let cancelled = false;
    setError('');
    clearSaveToast();
    setSelected(new Set());

    (async () => {
      const localConnected =
        settings.accountingProvider === 'QUICKBOOKS' && settings.accountingConnected;

      try {
        const status = await api.get(
          `/server/quickbooks_api/status?email=${encodeURIComponent(email)}`
        );
        const isConnected = status.connection?.status === 'connected' || localConnected;
        if (cancelled) return;
        setConnected(isConnected);

        if (!isConnected) {
          setInvoices([]);
          return;
        }

        const data = await api.get(
          `/server/quickbooks_api/invoices?email=${encodeURIComponent(email)}`
        );
        if (cancelled) return;
        setInvoices(data.invoices || []);
        setMock(Boolean(data.mock));
      } catch {
        if (cancelled) return;
        if (localConnected) {
          setConnected(true);
          setInvoices(demoInvoices());
          setMock(true);
        } else {
          setConnected(false);
          setInvoices([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, email, settings.accountingConnected, settings.accountingProvider]);

  const sendable = useMemo(
    () => invoices.filter((inv) => !inv.demandSent),
    [invoices]
  );

  const selectedCount = selected.size;

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllSendable() {
    if (selected.size === sendable.length) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(sendable.map((i) => i.id)));
  }

  function sendSelected() {
    if (!selectedCount) return;
    setError('');
    clearSaveToast();

    startTransition(async () => {
      const invoiceIds = Array.from(selected);
      const choices = demandChoiceFields(getLocalSettings(email || ''));
      try {
        const data = await api.post('/server/quickbooks_api/send-demand', {
          email,
          invoiceIds,
          autoRoute: true,
          ...choices,
          installmentPlan: choices.Installment_Plan,
          settlementChoice: choices.Settlement_Choice,
        });

        const sentMatters = (data.results || [])
          .filter((r) => !r.skipped && r.matter)
          .map((r) => ({
            ...(r.created ? choices : {}),
            ...r.matter,
            Origin: 'AUTOMATED',
            Entry_Tag: 'Automated',
            External_Invoice_ID: r.invoice?.externalInvoiceId || r.matter.External_Invoice_ID,
          }));

        flashSave(
          `Sent ${data.sent} demand${data.sent === 1 ? '' : 's'} from QuickBooks${
            data.skipped ? ` · ${data.skipped} already sent` : ''
          }.`
        );
        setInvoices((prev) =>
          prev.map((inv) =>
            invoiceIds.includes(inv.id)
              ? { ...inv, demandSent: true, imported: true, matterStatus: 'ATTORNEY_ASSIGNED' }
              : inv
          )
        );
        setSelected(new Set());
        onDemandsSent?.(sentMatters, data);
      } catch {
        // Local/demo send
        const chosen = invoices.filter((i) => invoiceIds.includes(i.id) && !i.demandSent);
        const sentMatters = chosen.map((inv) => ({
          ROWID: inv.matterId || `m_qb_send_${inv.id}_${Date.now()}`,
          Demand_ID: resolveCreateDemandId(),
          Debtor_Name: inv.debtorName,
          Debtor_State: inv.debtorState,
          Amount_Owed: Number(Number(inv.amountOwed).toFixed(2)),
          Status: 'ATTORNEY_ASSIGNED',
          Origin: 'AUTOMATED',
          Entry_Tag: 'Automated',
          External_Invoice_ID: inv.externalInvoiceId,
          Evidence_Vault_URL: inv.evidenceVaultUrl,
          Attorney_Name: 'Jordan Hale, Esq.',
          ...choices,
        }));

        try {
          const key = 'demandjet.qb.imported';
          const prev = JSON.parse(sessionStorage.getItem(key) || '[]');
          sessionStorage.setItem(key, JSON.stringify([...sentMatters, ...prev]));
        } catch {
          // ignore
        }

        setInvoices((prev) =>
          prev.map((inv) =>
            invoiceIds.includes(inv.id)
              ? { ...inv, demandSent: true, imported: true, matterStatus: 'ATTORNEY_ASSIGNED' }
              : inv
          )
        );
        setSelected(new Set());
        flashSave(
          `Sent ${sentMatters.length} demand${sentMatters.length === 1 ? '' : 's'} from QuickBooks (demo).`
        );
        setMock(true);
        onDemandsSent?.(sentMatters, { mock: true, sent: sentMatters.length });
      }
    });
  }

  if (!open) return null;

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside
        className="qb-send-drawer"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Send demands from QuickBooks"
      >
        <header className="matter-detail-header">
          <div>
            <p className="eyebrow">QuickBooks</p>
            <h2>Send demands</h2>
            <p>Select open invoices to send as DemandJet attorney demands.</p>
          </div>
          <button type="button" className="icon-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="qb-send-body">
          {!connected ? (
            <div className="connect-box">
              <h3>QuickBooks not connected</h3>
              <p>Connect your company file first, then return here to send demands.</p>
              <Link className="btn-primary conn-connect" to="/client/settings?tab=integrations">
                Open Connections
              </Link>
            </div>
          ) : (
            <>
              <div className="qb-send-toolbar">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={sendable.length > 0 && selected.size === sendable.length}
                    onChange={toggleAllSendable}
                    disabled={!sendable.length}
                  />
                  <span>Select all sendable</span>
                </label>
                {mock ? <span className="demo-pill">Demo invoices</span> : null}
              </div>

              {error ? <p className="form-error">{error}</p> : null}

              <ul className="qb-invoice-list">
                {invoices.map((inv) => {
                  const disabled = inv.demandSent;
                  const checked = selected.has(inv.id);
                  return (
                    <li key={inv.id} className={disabled ? 'is-sent' : ''}>
                      <label className="qb-invoice-row">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => toggle(inv.id)}
                        />
                        <div>
                          <strong>
                            {inv.debtorName}{' '}
                            <span className="qb-doc">INV-{inv.docNumber}</span>
                          </strong>
                          <span>
                            {inv.debtorState} · {formatCurrency(inv.amountOwed)}
                            {inv.dueDate ? ` · due ${inv.dueDate}` : ''}
                          </span>
                          <span className="qb-status-line">
                            {inv.demandSent
                              ? `Demand sent · ${inv.matterStatus || 'in pipeline'}`
                              : inv.imported
                                ? 'Ready to send (already in DemandJet)'
                                : 'Ready to send from QuickBooks'}
                          </span>
                        </div>
                        <em>{formatCurrency(inv.balance)}</em>
                      </label>
                    </li>
                  );
                })}
              </ul>

              {!invoices.length ? (
                <p className="empty-inline">No open QuickBooks invoices found.</p>
              ) : null}
            </>
          )}
        </div>

        {connected ? (
          <footer className="qb-send-footer">
            <p>
              {selectedCount
                ? `${selectedCount} invoice${selectedCount === 1 ? '' : 's'} selected`
                : 'Select invoices to send'}
            </p>
            <div className="drawer-actions">
              <button type="button" className="btn-ghost" onClick={onClose}>
                Close
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={pending || !selectedCount}
                onClick={sendSelected}
              >
                {pending
                  ? 'Sending…'
                  : `Send demand${selectedCount === 1 ? '' : 's'}`}
              </button>
            </div>
          </footer>
        ) : null}
      </aside>
    </div>
  );
}
